import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import ListInfoProvider from "lib/list/ListInfoProvider";
import TableActionbar from "lib/ui/table/TableActionbar";

import {
	ChangeEvent,
	DeleteEvent,
	Event,
	EventGroup,
	MoveEvent
} from "utils/history/Event";
import { asTitle } from "utils/Utils";

import { Field, GroupField } from "core/field/FieldService";
import { FormFieldExpansionProps } from "core/field/form/FormFieldRow";
import FormFieldTable from "core/field/form/FormFieldTable";

export type GroupFormFieldExpensionProps = FormFieldExpansionProps & {
	field: GroupField;
};

const GroupFormFieldExpansion: React.FC<GroupFormFieldExpensionProps> = (
	props: GroupFormFieldExpensionProps
) => {
	const { t } = useTranslation();

	const changeField = useCallback(
		(event: Event): void => {
			event.applyContext("/children");
			props.onChange(event);
		},
		[props.onChange]
	);

	const handleUpdate = useCallback(
		(
			keys: Array<string>,
			f: (index: number, field: Field) => Event
		): void => {
			const events: Event[] = [];
			for (const [index, field] of props.field.children.entries()) {
				if (keys.includes(field.uuid)) {
					events.push(f(index, field));
				}
			}
			const event = new EventGroup(events);
			props.onChange(event);
		},
		[props.field.children, props.onChange]
	);

	const handleMoveUp = useCallback(
		(keys: Array<string>): void => {
			handleUpdate(
				keys,
				index =>
					new MoveEvent(
						`/children/${index}`,
						`/children/${index - 1}`
					)
			);
		},
		[handleUpdate]
	);

	const handleMoveDown = useCallback(
		(keys: Array<string>): void => {
			handleUpdate(
				keys,
				index =>
					new MoveEvent(
						`/children/${index}`,
						`/children/${index + 1}`
					)
			);
		},
		[handleUpdate]
	);

	const handleDelete = useCallback(
		(keys: Array<string>): void => {
			handleUpdate(
				keys,
				(index, field) => new DeleteEvent(`/children/${index}`, field)
			);
		},
		[handleUpdate]
	);

	function toggleCollapsed(event: React.ChangeEvent<HTMLInputElement>): void {
		const change = new ChangeEvent(
			"/collapsed",
			props.field.collapsed,
			event.target.checked
		);
		props.onChange(change);
	}

	return (
		<Grid container>
			<Grid item container alignItems="center">
				<Grid item xs={6} sm={4} md={3} lg={2}>
					<Typography variant="subtitle2">
						{asTitle(t("collapsed"))}
					</Typography>
				</Grid>
				<Grid item xs={6} sm={4} md={3} lg={2}>
					<Checkbox
						checked={props.field.collapsed}
						onChange={toggleCollapsed}
					/>
				</Grid>
			</Grid>
			<Grid item>
				<Typography variant="subtitle2">
					{asTitle(t("elements"))}
				</Typography>
				<ListInfoProvider
					keys={props.field.children.map(field => field.uuid)}
					isEnabled={() => props.editable}>
					<TableActionbar
						onMoveUp={handleMoveUp}
						onMoveDown={handleMoveDown}
						onDelete={handleDelete}
					/>
					<FormFieldTable
						elements={props.field.children}
						editable={props.editable}
						onChange={changeField}
						onInsert={changeField}
					/>
				</ListInfoProvider>
			</Grid>
		</Grid>
	);
};

export default GroupFormFieldExpansion;
